/**
 * TanStack Query mutations для thoughts
 * Содержит все мутации для создания, обновления и удаления мыслей
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
	CreateThoughtVars,
	ThoughtIdVars,
	UpdateThoughtVars,
} from "../model/types"
import {
	createThought,
	deleteThought,
	markThoughtRead,
	updateThought,
} from "./client"
import { thoughtQueryOptions, thoughtsQueryOptions } from "./queries"

/**
 * Hook для создания новой мысли
 */
export function useCreateThought() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, data }: CreateThoughtVars) =>
			createThought(userId, data),
		onSuccess: (newThought) => {
			const detail = thoughtQueryOptions(newThought.user_id, newThought.id)
			queryClient.setQueryData(detail.queryKey, newThought)
			const list = thoughtsQueryOptions(newThought.user_id)
			queryClient.setQueryData(list.queryKey, (old) => {
				if (!old) return old
				return [newThought, ...old]
			})
		},
	})
}

/**
 * Hook для обновления записи
 */
export function useUpdateThought() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, thoughtId, data }: UpdateThoughtVars) =>
			updateThought(userId, thoughtId, data),
		onSuccess: (updatedThought) => {
			const detail = thoughtQueryOptions(
				updatedThought.user_id,
				updatedThought.id,
			)
			queryClient.setQueryData(detail.queryKey, updatedThought)
			const list = thoughtsQueryOptions(updatedThought.user_id)
			queryClient.setQueryData(list.queryKey, (old) =>
				old?.map((t) => {
					if (t.id === updatedThought.id) return updatedThought
					return t
				}),
			)
		},
	})
}

export function useMarkThoughtRead() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, thoughtId }: ThoughtIdVars) =>
			markThoughtRead(userId, thoughtId),
		onSuccess: (thought) => {
			const { queryKey } = thoughtsQueryOptions(thought.user_id)
			queryClient.setQueryData(queryKey, (old) =>
				old?.map((t) => {
					if (t.id === thought.id) return thought
					return t
				}),
			)
		},
	})
}

/**
 * Hook для удаления мысли
 */
export function useDeleteThought() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, thoughtId }: ThoughtIdVars) =>
			deleteThought(userId, thoughtId),
		onMutate: async ({ userId, thoughtId }) => {
			const { queryKey } = thoughtsQueryOptions(userId)
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData(queryKey)
			queryClient.setQueryData(queryKey, (old) =>
				old?.filter((t) => t.id !== thoughtId),
			)
			return { previous }
		},
		onError: (_err, { userId }, context) => {
			if (context?.previous) {
				const { queryKey } = thoughtsQueryOptions(userId)
				queryClient.setQueryData(queryKey, context.previous)
			}
		},
		onSuccess: (_, vars) => {
			queryClient.removeQueries({
				queryKey: thoughtQueryOptions(vars.userId, vars.thoughtId).queryKey,
			})
		},
		onSettled: (_data, _err, { userId }) => {
			queryClient.invalidateQueries({
				queryKey: thoughtsQueryOptions(userId).queryKey,
			})
		},
	})
}
